import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { type ColumnDef } from '@tanstack/react-table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  MoreHorizontal,
  Eye,
  Pencil,
  Trash2,
  FileText,
  MessageCircle,
  UserCheck,
  UserMinus,
  Users,
  Mail,
  Info,
  ListTodo,
} from 'lucide-react'
import {
  DataTable,
  ColumnVisibilityMenu,
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
} from '@/components/ui/data-table'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { useTablePreferences } from '@/hooks/useTablePreferences'
import { ImpuestosBadgeList } from '@/components/ui/impuesto-badge'
import { CopyableCell } from '@/components/ui/copyable-cell'
import { useSupabase } from '@/lib/supabase-context'
import { impuestosClienteService } from '@/services/impuestosClienteService'
import { impuestosClienteKeys } from '@/hooks/useImpuestosCliente'
import { CondicionFiscalBadge } from '@/components/clientes/CondicionFiscalBadge'
import { ServiciosBadgeList } from '@/components/ui/servicio-badge'
import { useEtiquetasCliente, useEtiquetasDeClientes } from '@/core/hooks/useEtiquetasCliente'
import { usePermissions } from '@/hooks/usePermissions'
import { AbrirAfipDirectoButton } from '@/components/clientes/AbrirAfipDirectoButton'
import { useClientesConClaveArca } from '@/components/clientes/useClientesConClaveArca'
import type { VistaSaldoClienteConImpuestos } from '@/services/clientesService'
import type { ColorImpuesto } from '@/types/impuestos'
import { generarEnlaceWhatsApp, formatCurrency, calcularTotalMensual, formatDateOrDash as formatDate } from '@/lib/utils'

// Helper functions
function formatCuit(cuit: number): string {
  const str = cuit.toString().padStart(11, '0')
  return `${str.slice(0, 2)}-${str.slice(2, 10)}-${str.slice(10)}`
}

function formatCuitSinGuiones(cuit: number): string {
  return cuit.toString().padStart(11, '0')
}

function formatIdCliente(numero: number | null | undefined): string {
  if (!numero) return '-'
  return `CLI-${String(numero).padStart(3, '0')}`
}

/** Etiquetas del cliente como badges compactos, con "+N" si hay más de `max`. */
function EtiquetasBadgeList({ nombres, max = 2 }: { nombres: string[]; max?: number }) {
  if (nombres.length === 0) return <span className="text-muted-foreground text-sm">-</span>

  const mostrar = nombres.slice(0, max)
  const ocultos = nombres.length - max

  return (
    <div className="flex items-center gap-1 flex-wrap">
      {mostrar.map((nombre) => (
        <Badge key={nombre} variant="secondary" className="text-xs font-normal">
          {nombre}
        </Badge>
      ))}
      {ocultos > 0 && (
        <span className="text-xs text-muted-foreground font-medium" title={nombres.slice(max).join(', ')}>
          +{ocultos}
        </span>
      )}
    </div>
  )
}

function EstadoBadge({ estado }: { estado: string }) {
  const estadoLower = estado?.toLowerCase()
  const variant = estadoLower === 'activo' ? 'default' : 'secondary'
  const label = estadoLower === 'activo' ? 'activo' : 'inactivo'
  return <Badge variant={variant}>{label}</Badge>
}

function TipoPersonaBadge({ cuit, tipo }: { cuit: number | null; tipo?: string | null }) {
  if (tipo === 'sucesion_indivisa') {
    return (
      <span
        className="text-[10px] px-1.5 py-0.5 rounded font-medium shrink-0 bg-purple-100 text-purple-700 dark:bg-purple-950 dark:text-purple-300"
        title="Sucesión Indivisa"
      >
        SI
      </span>
    )
  }

  if (!cuit) return null

  const str = cuit.toString().padStart(11, '0')
  const prefijo = str.slice(0, 2)
  const esJuridica = ['30', '33', '34'].includes(prefijo)

  if (tipo === 'juridica') {
    return (
      <span
        className="text-[10px] px-1.5 py-0.5 rounded font-medium shrink-0 bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300"
        title="Sociedad"
      >
        PJ
      </span>
    )
  }

  return (
    <span
      className={`text-[10px] px-1.5 py-0.5 rounded font-medium shrink-0 ${
        esJuridica
          ? 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300'
          : 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300'
      }`}
      title={esJuridica ? 'Sociedad' : 'Persona Física'}
    >
      {esJuridica ? 'PJ' : 'PF'}
    </span>
  )
}

interface ClientesTableProps {
  clientes: VistaSaldoClienteConImpuestos[]
  onVerDetalle: (cliente: VistaSaldoClienteConImpuestos) => void
  onEditar: (cliente: VistaSaldoClienteConImpuestos) => void
  onEliminar: (cliente: VistaSaldoClienteConImpuestos) => void
  onVerEstadoCuenta: (cliente: VistaSaldoClienteConImpuestos) => void
  onCambiarEstado: (cliente: VistaSaldoClienteConImpuestos, nuevoEstado: 'Activo' | 'Inactivo') => void
  onEnviarContrato: (cliente: VistaSaldoClienteConImpuestos) => void
  /** ORR-327: click en la fila abre la ficha. Se ignora con selectionMode activo. */
  onRowClick?: (cliente: VistaSaldoClienteConImpuestos) => void
  /** ORR-327: alta de tarea con este cliente ya asignado. */
  onCargarTarea?: (cliente: VistaSaldoClienteConImpuestos) => void
  ultimosEnvios?: Record<string, string>
  isLoading?: boolean
  selectionMode?: boolean
  selectedIds?: Set<string>
  onSelectionChange?: (id: string, selected: boolean) => void
  canEdit?: boolean
  canDelete?: boolean
  canCreateTarea?: boolean
  /** Muestra la columna Importe, los precios de los servicios y la opción Estado de cuenta. */
  mostrarMontos?: boolean
  /** Ofrece «Estado de cuenta»: es la cuenta corriente, va con verMovimientos (ORR-613). */
  mostrarEstadoCuenta?: boolean
}

export function ClientesTable({
  clientes,
  onVerDetalle,
  onEditar,
  onEliminar,
  onVerEstadoCuenta,
  onCambiarEstado,
  onEnviarContrato,
  onRowClick,
  onCargarTarea,
  ultimosEnvios,
  isLoading,
  selectionMode,
  selectedIds,
  onSelectionChange,
  canEdit = true,
  canDelete = true,
  canCreateTarea = false,
  mostrarMontos = false,
  mostrarEstadoCuenta = false,
}: ClientesTableProps) {
  const { supabase } = useSupabase()

  const clienteIds = useMemo(
    () => clientes.map((c) => c.id).filter((id): id is string => id !== null),
    [clientes]
  )

  const { data: impuestosMap } = useQuery({
    queryKey: impuestosClienteKeys.batch(clienteIds),
    queryFn: () => impuestosClienteService.listarPorClientes(supabase!, clienteIds),
    enabled: !!supabase && clienteIds.length > 0,
  })

  // Una consulta por organización (no por fila) + el catálogo para resolver nombres. Misma
  // query key que en `Clientes.tsx`, así que React Query la comparte en vez de duplicarla.
  const { data: catalogoEtiquetas = [] } = useEtiquetasCliente()
  const { data: etiquetasPorCliente } = useEtiquetasDeClientes()
  // Una sola consulta para todo el listado: qué clientes tienen clave de ARCA vigente.
  const { puede } = usePermissions()
  const { data: clientesConClaveArca } = useClientesConClaveArca(puede.letritaA)

  const nombrePorIdEtiqueta = useMemo(
    () => new Map(catalogoEtiquetas.map((e) => [e.id, e.nombre])),
    [catalogoEtiquetas]
  )

  const {
    visibility,
    saveVisibility,
    columnSizing,
    saveColumnSizing,
    resetVisibility,
    resetColumnSizing,
    resetAll,
  } = useTablePreferences('clientes', {
    defaultVisibility: {
      id_cliente: true,
      nombre: true,
      cuit: true,
      condicion_fiscal: true,
      servicios: true,
      etiquetas: true,
      importe_total: true,
      responsable: true,
      estado: true,
      observaciones: true,
      // Ocultas por defecto
      impuestos: true,
      cierre_ejercicio: false,
      email: false,
      telefono: false,
      provincia: false,
      actividad: false,
      cliente_desde: false,
    },
  })

  const columns = useMemo<ColumnDef<VistaSaldoClienteConImpuestos>[]>(
    () => [
      // 0. Checkbox de selección (condicional)
      ...(selectionMode ? [{
        id: 'seleccionar',
        header: () => null,
        cell: ({ row }: { row: { original: VistaSaldoClienteConImpuestos } }) => {
          const id = row.original.id
          if (!id) return null
          return (
            <input
              type="checkbox"
              checked={selectedIds?.has(id) ?? false}
              onChange={(e) => {
                e.stopPropagation()
                onSelectionChange?.(id, e.target.checked)
              }}
              onClick={(e) => e.stopPropagation()}
              className="h-4 w-4 rounded border-gray-300 cursor-pointer"
            />
          )
        },
        size: 40,
        enableSorting: false,
        enableResizing: false,
      } as ColumnDef<VistaSaldoClienteConImpuestos>] : []),

      // 1. ID Cliente (CLI-XXX)
      {
        id: 'id_cliente',
        accessorKey: 'numero_cliente',
        header: 'ID',
        meta: { label: 'ID' },
        size: 80,
        minSize: 60,
        maxSize: 120,
        cell: ({ row }) => (
          <span className="text-muted-foreground font-mono text-sm">
            {formatIdCliente(row.original.numero_cliente)}
          </span>
        ),
      },
      // 2. Apellido y nombre + badge tipo
      {
        id: 'nombre',
        accessorKey: 'nombre_formateado',
        header: 'Apellido y nombre',
        meta: { label: 'Apellido y nombre' },
        size: 200,
        minSize: 120,
        maxSize: 400,
        cell: ({ row }) => {
          const cliente = row.original
          const nombreMostrar = cliente.nombre_formateado || cliente.nombre || '-'
          return (
            <div className="flex items-center gap-2 min-w-0">
              <span className="truncate font-medium">{nombreMostrar}</span>
              <TipoPersonaBadge cuit={cliente.cuit} tipo={cliente.tipo_persona} />
            </div>
          )
        },
      },
      // 3. CUIT (click to copy sin guiones) + CUIT Admin en popover para SI/PJ
      {
        id: 'cuit',
        accessorKey: 'cuit',
        header: 'CUIT',
        meta: { label: 'CUIT' },
        size: 150,
        minSize: 120,
        maxSize: 180,
        cell: ({ row }) => {
          const cuit = row.original.cuit
          const tipo = row.original.tipo_persona
          const adminCuit = row.original.admin_cuit

          if (!cuit) return <span className="text-muted-foreground">-</span>

          // Verificar si tiene admin (SI o PJ con CUIT admin)
          const tieneAdmin = (tipo === 'sucesion_indivisa' || tipo === 'juridica') && adminCuit

          // Limpiar y formatear CUIT admin (evita cálculo redundante)
          const adminCuitLimpio = adminCuit
            ? adminCuit.replace(/\D/g, '').padStart(11, '0')
            : null
          const adminCuitFormateado = adminCuitLimpio
            ? `${adminCuitLimpio.slice(0, 2)}-${adminCuitLimpio.slice(2, 10)}-${adminCuitLimpio.slice(10)}`
            : null
          const labelAdmin = tipo === 'sucesion_indivisa' ? 'Administrador' : 'Rep. Legal'

          return (
            <div className="flex items-center gap-1">
              <CopyableCell
                value={formatCuitSinGuiones(cuit)}
                displayValue={formatCuit(cuit)}
              />
              {tieneAdmin && (
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="inline-flex items-center justify-center h-5 w-5 rounded hover:bg-muted transition-colors"
                      title={`Ver CUIT ${labelAdmin}`}
                      aria-label={`Ver CUIT ${labelAdmin}`}
                    >
                      <Users className="h-3.5 w-3.5 text-muted-foreground" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    side="top"
                    align="start"
                    className="w-auto p-3"
                  >
                    <div className="space-y-1">
                      <p className="text-xs text-muted-foreground">{labelAdmin}</p>
                      <CopyableCell
                        value={adminCuitLimpio || ''}
                        displayValue={adminCuitFormateado || ''}
                        className="font-mono"
                      />
                    </div>
                  </PopoverContent>
                </Popover>
              )}
            </div>
          )
        },
      },
      // 4. Condicion Fiscal (badge abreviado)
      {
        id: 'condicion_fiscal',
        accessorKey: 'condicion_iva',
        header: 'Cond. Fiscal',
        meta: { label: 'Condición Fiscal' },
        size: 100,
        minSize: 80,
        maxSize: 150,
        cell: ({ row }) => (
          <CondicionFiscalBadge condicion={row.original.condicion_iva} />
        ),
      },
      // 5. Servicios (badges + popover)
      {
        id: 'servicios',
        accessorKey: 'servicios_activos',
        header: 'Servicios',
        meta: { label: 'Servicios Contratados' },
        size: 200,
        minSize: 150,
        maxSize: 300,
        cell: ({ row }) => {
          const servicios = row.original.servicios_activos
          return <ServiciosBadgeList servicios={servicios || []} max={2} mostrarPrecios={mostrarMontos} />
        },
      },
      // 5b. Etiquetas
      {
        id: 'etiquetas',
        header: 'Etiquetas',
        meta: { label: 'Etiquetas' },
        size: 160,
        minSize: 100,
        maxSize: 260,
        cell: ({ row }) => {
          const id = row.original.id
          const ids = (id && etiquetasPorCliente?.get(id)) || []
          const nombres = ids.map((eid) => nombrePorIdEtiqueta.get(eid)).filter((n): n is string => !!n)
          return <EtiquetasBadgeList nombres={nombres} max={2} />
        },
      },
      // 6. Importe Total (equivalente mensual)
      ...(mostrarMontos ? [{
        id: 'importe_total',
        accessorKey: 'servicios_activos',
        header: 'Importe',
        meta: { label: 'Importe Total' },
        size: 120,
        minSize: 100,
        maxSize: 150,
        cell: ({ row }: { row: { original: VistaSaldoClienteConImpuestos } }) => {
          const servicios = row.original.servicios_activos
          const total = calcularTotalMensual(servicios || [])
          const ultimoEnvio = ultimosEnvios?.[row.original.id ?? '']
          if (total === 0 && !ultimoEnvio) {
            return <span className="text-muted-foreground">-</span>
          }
          return (
            <div>
              {total > 0 && (
                <span className="font-medium tabular-nums">
                  {formatCurrency(total)}
                </span>
              )}
              {ultimoEnvio && (
                <p className="text-xs text-muted-foreground">
                  <Mail className="h-3 w-3 inline mr-1" />
                  Enviado: {new Date(ultimoEnvio).toLocaleDateString('es-AR')}
                </p>
              )}
            </div>
          )
        },
      } as ColumnDef<VistaSaldoClienteConImpuestos>] : []),
      // 7. Impuestos
      {
        id: 'impuestos',
        header: 'Impuestos',
        meta: { label: 'Impuestos' },
        size: 150,
        minSize: 120,
        maxSize: 250,
        cell: ({ row }) => {
          const clienteImpuestos = row.original.id ? impuestosMap?.get(row.original.id) : undefined
          if (!clienteImpuestos?.length) {
            return <span className="text-muted-foreground text-sm">-</span>
          }
          const impuestosResueltos = clienteImpuestos.map((ic) => ({
            nombre_corto: ic.impuesto.nombre_corto || ic.impuesto.nombre,
            color: (ic.impuesto.color || 'gray') as ColorImpuesto,
          }))
          return <ImpuestosBadgeList impuestos={impuestosResueltos} max={2} />
        },
      },
      // 8. Cierre de Ejercicio (solo PJ, SI muestra "-")
      {
        id: 'cierre_ejercicio',
        accessorKey: 'cierre_ejercicio',
        header: 'Cierre Ej.',
        meta: { label: 'Cierre de Ejercicio' },
        size: 90,
        minSize: 70,
        maxSize: 120,
        cell: ({ row }) => {
          const tipo = row.original.tipo_persona
          if (tipo === 'fisica') return <span className="text-muted-foreground">-</span>
          return row.original.cierre_ejercicio || <span className="text-muted-foreground">-</span>
        },
      },
      // 9. Email (click to copy)
      {
        id: 'email',
        accessorKey: 'email',
        header: 'Email',
        meta: { label: 'Email' },
        size: 180,
        minSize: 120,
        maxSize: 300,
        cell: ({ row }) => {
          const email = row.original.email
          if (!email) return <span className="text-muted-foreground">-</span>
          return <CopyableCell value={email} className="text-sm" />
        },
      },
      // 10. Telefono (click to copy)
      {
        id: 'telefono',
        accessorKey: 'telefono',
        header: 'Telefono',
        meta: { label: 'Telefono' },
        size: 120,
        minSize: 100,
        maxSize: 180,
        cell: ({ row }) => {
          const tel = row.original.telefono
          if (!tel) return <span className="text-muted-foreground">-</span>
          return <CopyableCell value={tel} className="text-sm" />
        },
      },
      // 11. Provincia
      {
        id: 'provincia',
        accessorKey: 'provincia',
        header: 'Provincia',
        meta: { label: 'Provincia' },
        size: 120,
        minSize: 80,
        maxSize: 180,
        cell: ({ row }) => (
          <span className="truncate block">
            {row.original.provincia || <span className="text-muted-foreground">-</span>}
          </span>
        ),
      },
      // 12. Actividad Principal (código AFIP con popover clickeable)
      {
        id: 'actividad',
        accessorKey: 'codigo_actividad_afip',
        header: 'Actividad',
        meta: { label: 'Actividad Principal' },
        size: 100,
        minSize: 80,
        maxSize: 200,
        cell: ({ row }) => {
          const codigo = row.original.codigo_actividad_afip
          const descripcion = row.original.actividad_principal
          if (!codigo && !descripcion) return <span className="text-muted-foreground">-</span>
          // Si hay código y descripción, mostrar con popover clickeable
          if (codigo && descripcion) {
            return (
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 font-mono text-sm cursor-pointer underline decoration-dotted underline-offset-2 hover:text-primary transition-colors max-w-full"
                  >
                    <span className="truncate">{codigo}</span>
                    <Info className="h-3 w-3 text-muted-foreground shrink-0" />
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  side="top"
                  align="start"
                  className="max-w-xs animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95"
                >
                  <p className="text-sm">{descripcion}</p>
                </PopoverContent>
              </Popover>
            )
          }
          // Solo código sin descripción
          if (codigo) {
            return <span className="font-mono text-sm truncate block">{codigo}</span>
          }
          // Fallback si solo hay descripción
          return (
            <span title={descripcion || ''} className="text-sm truncate block">
              {descripcion}
            </span>
          )
        },
      },
      // 13. Cliente Desde (fecha)
      {
        id: 'cliente_desde',
        accessorKey: 'fecha_alta',
        header: 'Cliente Desde',
        meta: { label: 'Cliente Desde' },
        size: 110,
        minSize: 90,
        maxSize: 140,
        cell: ({ row }) => (
          <span className="text-sm">{formatDate(row.original.fecha_alta)}</span>
        ),
      },
      // 14. Responsable
      {
        id: 'responsable',
        accessorKey: 'responsable',
        header: 'Responsable',
        meta: { label: 'Responsable' },
        size: 120,
        minSize: 80,
        maxSize: 200,
        cell: ({ row }) => (
          <span className="truncate block">
            {row.original.responsable || <span className="text-muted-foreground">-</span>}
          </span>
        ),
      },
      // 15. Estado
      {
        id: 'estado',
        accessorKey: 'estado',
        header: 'Estado',
        meta: { label: 'Estado' },
        size: 80,
        minSize: 70,
        maxSize: 120,
        cell: ({ row }) => <EstadoBadge estado={row.original.estado || 'activo'} />,
      },
      // 16. Observaciones
      {
        id: 'observaciones',
        accessorKey: 'descripcion',
        header: 'Obs',
        meta: { label: 'Observaciones' },
        size: 150,
        minSize: 80,
        maxSize: 300,
        cell: ({ row }) => {
          const obs = row.original.descripcion
          if (!obs) return <span className="text-muted-foreground">-</span>
          const truncated = obs.length > 30 ? `${obs.slice(0, 30)}...` : obs
          return (
            <span className="text-sm" title={obs}>
              {truncated}
            </span>
          )
        },
      },
      // 17. Acciones
      {
        id: 'acciones',
        header: '',
        enableHiding: false,
        enableSorting: false,
        enableResizing: false,
        size: puede.letritaA ? 88 : 50,
        minSize: puede.letritaA ? 88 : 50,
        maxSize: puede.letritaA ? 88 : 50,
        cell: ({ row }) => {
          const cliente = row.original
          return (
            <div className="flex items-center" onClick={(e) => e.stopPropagation()}>
              {puede.letritaA && cliente.id && clientesConClaveArca?.has(cliente.id) && (
                <AbrirAfipDirectoButton
                  clienteId={cliente.id}
                  nombreCliente={cliente.nombre_formateado || cliente.nombre || ''}
                  tieneClave
                  compacto
                />
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => onVerDetalle(cliente)}>
                    <Eye className="mr-2 h-4 w-4" />
                    Ver detalle
                  </DropdownMenuItem>
                  {mostrarEstadoCuenta && (
                    <DropdownMenuItem onClick={() => onVerEstadoCuenta(cliente)}>
                      <FileText className="mr-2 h-4 w-4" />
                      Estado de cuenta
                    </DropdownMenuItem>
                  )}
                  {canCreateTarea && onCargarTarea && (
                    <DropdownMenuItem onClick={() => onCargarTarea(cliente)}>
                      <ListTodo className="mr-2 h-4 w-4" />
                      Cargar tarea
                    </DropdownMenuItem>
                  )}
                  {cliente.telefono && (
                    <DropdownMenuItem
                      onClick={() => {
                        const url = generarEnlaceWhatsApp(cliente.telefono)
                        if (url) window.open(url, '_blank')
                      }}
                    >
                      <MessageCircle className="mr-2 h-4 w-4 text-green-600" />
                      WhatsApp
                    </DropdownMenuItem>
                  )}
                  {canEdit && cliente.email && (
                    <DropdownMenuItem onClick={() => onEnviarContrato(cliente)}>
                      <Mail className="mr-2 h-4 w-4" />
                      Enviar Contrato
                    </DropdownMenuItem>
                  )}
                  {(canEdit || canDelete) && <DropdownMenuSeparator />}
                  {canEdit && (
                    <DropdownMenuItem onClick={() => onEditar(cliente)}>
                      <Pencil className="mr-2 h-4 w-4" />
                      Editar
                    </DropdownMenuItem>
                  )}
                  {canEdit && (
                    cliente.estado === 'activo' ? (
                      <DropdownMenuItem
                        onClick={() => onCambiarEstado(cliente, 'Inactivo')}
                        className="text-amber-600"
                      >
                        <UserMinus className="mr-2 h-4 w-4" />
                        Dar de Baja
                      </DropdownMenuItem>
                    ) : (
                      <DropdownMenuItem
                        onClick={() => onCambiarEstado(cliente, 'Activo')}
                        className="text-green-600"
                      >
                        <UserCheck className="mr-2 h-4 w-4" />
                        Reactivar
                      </DropdownMenuItem>
                    )
                  )}
                  {canDelete && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => onEliminar(cliente)}
                        className="text-red-600"
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Eliminar
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )
        },
      },
    ],
    [onVerDetalle, onEditar, onEliminar, onVerEstadoCuenta, onCambiarEstado, onEnviarContrato, onCargarTarea, impuestosMap, etiquetasPorCliente, nombrePorIdEtiqueta, ultimosEnvios, selectionMode, selectedIds, onSelectionChange, canEdit, canDelete, canCreateTarea, puede.letritaA, clientesConClaveArca, mostrarMontos, mostrarEstadoCuenta]
  )

  const table = useReactTable({
    data: clientes,
    columns,
    state: {
      columnVisibility: visibility,
      columnSizing,
    },
    onColumnVisibilityChange: saveVisibility,
    onColumnSizingChange: saveColumnSizing,
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    initialState: {
      sorting: [{ id: 'id_cliente', desc: false }],
      pagination: { pageSize: 50 },
    },
  })

  return (
    <div className="h-full flex flex-col gap-4">
      <div className="flex justify-end shrink-0">
        <ColumnVisibilityMenu
          table={table}
          onResetVisibility={resetVisibility}
          onResetColumnSizing={resetColumnSizing}
          onResetAll={resetAll}
        />
      </div>
      <div className="flex-1 min-h-0">
        <DataTable
          table={table}
          isLoading={isLoading}
          emptyMessage="No hay clientes para mostrar"
          emptyIcon={<Users className="h-6 w-6 text-muted-foreground" />}
          withVerticalDividers
          stickyRightColumns={['acciones']}
          enableColumnResizing
          enableVerticalScroll
          showPagination
          onRowClick={selectionMode ? undefined : onRowClick}
        />
      </div>
    </div>
  )
}
